import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const FRICTION_CONTACT_COUNT = 8;
export const FRICTION_OUTPUT_CEILING = 2;
export const FRICTION_MATERIALS = Object.freeze(['bow', 'wood', 'metal', 'paper', 'fabric'] as const);
export type FrictionMaterial = (typeof FRICTION_MATERIALS)[number];

export interface FrictionContactParameters {
  readonly active: boolean;
  readonly material: FrictionMaterial;
  readonly pressure: number;
  readonly speed: number;
  readonly roughness: number;
  readonly level: number;
  readonly pan: number;
  readonly smoothingTimeSeconds: number;
  readonly seed: number;
}

export interface FrictionBowExciterOptions {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
}

export interface FrictionContactState {
  readonly pressure: number;
  readonly speed: number;
  readonly sticking: boolean;
  readonly transitionCount: number;
}

type Mutable<T> = { -readonly [Key in keyof T]: T[Key] };

interface MaterialModel {
  readonly staticFriction: number;
  readonly dynamicFriction: number;
  readonly velocityScale: number;
  readonly stickFraction: number;
  readonly cycleRate: number;
  readonly grainDensity: number;
  readonly colour: number;
  readonly resonanceHz: number;
  readonly resonanceDecay: number;
  readonly gain: number;
}

interface Contact {
  readonly parameters: Mutable<FrictionContactParameters>;
  currentPressure: number;
  currentSpeed: number;
  releaseGain: number;
  releaseSamples: number;
  phase: number;
  sticking: boolean;
  transitionCount: number;
  rng: number;
  previousRaw: number;
  highPass: number;
  colourState: number;
  grainEnvelope: number;
  resonance1: number;
  resonance2: number;
}

const DEFAULT_SEED = 0x6d2b79f5;
const RELEASE_SECONDS = 0.002;
const DEFAULTS: Readonly<FrictionContactParameters> = Object.freeze({
  active: false,
  material: 'bow',
  pressure: 0,
  speed: 0,
  roughness: 0,
  level: 1,
  pan: 0,
  smoothingTimeSeconds: 0.01,
  seed: 1,
});

const MATERIAL_MODELS: Readonly<Record<FrictionMaterial, MaterialModel>> = Object.freeze({
  bow: Object.freeze({ staticFriction: 1, dynamicFriction: 0.56, velocityScale: 0.19, stickFraction: 0.68, cycleRate: 58, grainDensity: 520, colour: 0.14, resonanceHz: 310, resonanceDecay: 0.955, gain: 0.82 }),
  wood: Object.freeze({ staticFriction: 0.88, dynamicFriction: 0.48, velocityScale: 0.25, stickFraction: 0.52, cycleRate: 91, grainDensity: 1050, colour: 0.32, resonanceHz: 760, resonanceDecay: 0.94, gain: 0.62 }),
  metal: Object.freeze({ staticFriction: 0.7, dynamicFriction: 0.34, velocityScale: 0.34, stickFraction: 0.34, cycleRate: 147, grainDensity: 1750, colour: 0.82, resonanceHz: 2650, resonanceDecay: 0.975, gain: 0.54 }),
  paper: Object.freeze({ staticFriction: 0.5, dynamicFriction: 0.27, velocityScale: 0.42, stickFraction: 0.23, cycleRate: 223, grainDensity: 3300, colour: 0.96, resonanceHz: 4700, resonanceDecay: 0.9, gain: 0.42 }),
  fabric: Object.freeze({ staticFriction: 1.08, dynamicFriction: 0.68, velocityScale: 0.13, stickFraction: 0.79, cycleRate: 39, grainDensity: 310, colour: 0.06, resonanceHz: 170, resonanceDecay: 0.925, gain: 0.94 }),
});

const bounded = (name: string, value: number, minimum: number, maximum: number): number => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${name} must be finite and in [${minimum}, ${maximum}]`);
  }
  return value;
};

const integer = (name: string, value: number, minimum: number, maximum: number): number => {
  bounded(name, value, minimum, maximum);
  if (!Number.isInteger(value)) throw new RangeError(`${name} must be an integer`);
  return value;
};

const validateMaterial = (material: FrictionMaterial): FrictionMaterial => {
  if (!(FRICTION_MATERIALS as readonly string[]).includes(material)) throw new RangeError('material is invalid');
  return material;
};

const canonicalSeed = (seed: number): number => seed === 0 ? DEFAULT_SEED : seed >>> 0;
const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(maximum, value));
const flush = (value: number): number => Math.abs(value) < 1e-24 ? 0 : value;

const validateParameters = (parameters: Readonly<FrictionContactParameters>): void => {
  if (typeof parameters.active !== 'boolean') throw new TypeError('active must be boolean');
  validateMaterial(parameters.material);
  bounded('pressure', parameters.pressure, 0, 1);
  bounded('speed', parameters.speed, -1, 1);
  bounded('roughness', parameters.roughness, 0, 1);
  bounded('level', parameters.level, 0, 1);
  bounded('pan', parameters.pan, -1, 1);
  bounded('smoothingTimeSeconds', parameters.smoothingTimeSeconds, 0, 0.1);
  integer('seed', parameters.seed, 0, 0xffff_ffff);
};

const validateBuffers = (
  left: Float32Array,
  right: Float32Array,
  frames: number,
  maxBlockSize: number,
): void => {
  if (!(left instanceof Float32Array) || !(right instanceof Float32Array)) {
    throw new TypeError('left and right must be Float32Array caller buffers');
  }
  if (!Number.isInteger(frames) || frames < 0 || frames > maxBlockSize || left.length < frames || right.length < frames) {
    throw new RangeError('caller buffers must cover frames within maxBlockSize');
  }
};

const nextNoise = (contact: Contact): number => {
  let value = contact.rng >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  contact.rng = value >>> 0;
  return contact.rng / 0x8000_0000 - 1;
};

/**
 * Eight-contact, host-independent stick/slip and roughness excitation source.
 * It deliberately models only the contact signal: string and body resonance
 * belong to downstream modules in the routing graph.
 */
export class FrictionBowExciter {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
  private readonly contacts: readonly Contact[];
  private readonly releaseLength: number;

  constructor(options: FrictionBowExciterOptions) {
    this.sampleRate = bounded('sampleRate', options.sampleRate, 8_000, 192_000);
    this.maxBlockSize = integer('maxBlockSize', options.maxBlockSize, 1, 8192);
    this.releaseLength = Math.max(1, Math.round(RELEASE_SECONDS * this.sampleRate));
    this.contacts = Array.from({ length: FRICTION_CONTACT_COUNT }, (_, index) => this.createContact(index + 1));
  }

  getContactParameters(index: number): Readonly<FrictionContactParameters> {
    return Object.freeze({ ...this.contact(index).parameters });
  }

  getContactState(index: number): Readonly<FrictionContactState> {
    const contact = this.contact(index);
    return Object.freeze({
      pressure: contact.currentPressure,
      speed: contact.currentSpeed,
      sticking: contact.sticking,
      transitionCount: contact.transitionCount,
    });
  }

  setContactParameters(index: number, update: Partial<FrictionContactParameters>): void {
    const contact = this.contact(index);
    const next = { ...contact.parameters, ...update };
    validateParameters(next);

    const wasActive = contact.parameters.active;
    const seedChanged = next.seed !== contact.parameters.seed;
    Object.assign(contact.parameters, next);
    if (seedChanged) contact.rng = canonicalSeed(next.seed);
    if (next.active && !wasActive) {
      contact.releaseGain = 1;
      contact.releaseSamples = 0;
    } else if (!next.active && wasActive) {
      contact.releaseGain = 1;
      contact.releaseSamples = this.releaseLength;
    }
  }

  reset(index?: number): void {
    if (index === undefined) {
      for (const contact of this.contacts) this.resetContact(contact);
    } else {
      this.resetContact(this.contact(index));
    }
  }

  processInto(left: Float32Array, right: Float32Array, frames = left.length): void {
    validateBuffers(left, right, frames, this.maxBlockSize);
    for (let frame = 0; frame < frames; frame += 1) {
      let mixedLeft = 0;
      let mixedRight = 0;
      let renderedContacts = 0;
      for (let index = 0; index < FRICTION_CONTACT_COUNT; index += 1) {
        const contact = this.contacts[index]!;
        if (!contact.parameters.active && contact.releaseSamples === 0) continue;
        const sample = this.processContact(contact);
        const angle = (contact.parameters.pan + 1) * Math.PI * 0.25;
        mixedLeft += sample * Math.cos(angle);
        mixedRight += sample * Math.sin(angle);
        renderedContacts += 1;
      }
      const normalization = renderedContacts > 0 ? 1 / Math.sqrt(renderedContacts) : 1;
      left[frame] = clamp(Number.isFinite(mixedLeft) ? mixedLeft * normalization : 0, -FRICTION_OUTPUT_CEILING, FRICTION_OUTPUT_CEILING);
      right[frame] = clamp(Number.isFinite(mixedRight) ? mixedRight * normalization : 0, -FRICTION_OUTPUT_CEILING, FRICTION_OUTPUT_CEILING);
    }
  }

  private processContact(contact: Contact): number {
    const parameters = contact.parameters;
    if (parameters.smoothingTimeSeconds === 0) {
      contact.currentPressure = parameters.pressure;
      contact.currentSpeed = parameters.speed;
    } else {
      const coefficient = Math.exp(-1 / (parameters.smoothingTimeSeconds * this.sampleRate));
      contact.currentPressure = flush(parameters.pressure + coefficient * (contact.currentPressure - parameters.pressure));
      contact.currentSpeed = flush(parameters.speed + coefficient * (contact.currentSpeed - parameters.speed));
    }

    const model = MATERIAL_MODELS[parameters.material];
    const absoluteSpeed = Math.abs(contact.currentSpeed);
    const direction = Math.sign(contact.currentSpeed);
    const phaseBefore = contact.phase;
    contact.phase += absoluteSpeed * model.cycleRate / this.sampleRate;
    if (contact.phase >= 1) {
      contact.phase -= Math.floor(contact.phase);
      contact.transitionCount += 2;
    }
    const sticking = contact.phase < model.stickFraction;
    if (sticking !== contact.sticking && phaseBefore !== contact.phase) {
      contact.sticking = sticking;
      contact.transitionCount += 1;
    }

    const speedCurve = model.dynamicFriction
      + (model.staticFriction - model.dynamicFriction) * Math.exp(-((absoluteSpeed / model.velocityScale) ** 2));
    const stickPosition = sticking
      ? contact.phase / model.stickFraction
      : (contact.phase - model.stickFraction) / (1 - model.stickFraction);
    const frictionShape = sticking ? -1 + 2 * stickPosition : 1 - 2 * stickPosition;
    const raw = direction * frictionShape * speedCurve * contact.currentPressure;
    contact.highPass = flush(raw - contact.previousRaw + 0.985 * contact.highPass);
    contact.previousRaw = raw;

    let grains = 0;
    if (absoluteSpeed > 1e-12 && contact.currentPressure > 1e-12) {
      const noise = nextNoise(contact);
      const probability = Math.min(0.75, model.grainDensity * parameters.roughness * absoluteSpeed / this.sampleRate);
      if ((noise + 1) * 0.5 < probability) contact.grainEnvelope = Math.max(contact.grainEnvelope, 0.4 + 0.6 * parameters.roughness);
      const colourCoefficient = 0.02 + (1 - model.colour) * 0.94;
      contact.colourState = flush(colourCoefficient * contact.colourState + (1 - colourCoefficient) * noise);
      grains = (model.colour > 0.5 ? noise - contact.colourState : contact.colourState) * contact.grainEnvelope;
      contact.grainEnvelope *= 0.82 + model.colour * 0.13;
    } else {
      contact.grainEnvelope *= 0.8;
      contact.colourState *= 0.8;
    }

    const omega = 2 * Math.PI * Math.min(model.resonanceHz, this.sampleRate * 0.44) / this.sampleRate;
    const resonanceInput = grains * parameters.roughness * contact.currentPressure * 0.12;
    const resonance = resonanceInput
      + 2 * model.resonanceDecay * Math.cos(omega) * contact.resonance1
      - model.resonanceDecay * model.resonanceDecay * contact.resonance2;
    contact.resonance2 = contact.resonance1;
    contact.resonance1 = flush(clamp(Number.isFinite(resonance) ? resonance : 0, -4, 4));

    let release = contact.releaseGain;
    if (contact.releaseSamples > 0) {
      contact.releaseSamples -= 1;
      release = contact.releaseSamples / this.releaseLength;
      contact.releaseGain = release;
      if (contact.releaseSamples === 0) contact.releaseGain = 0;
    }
    const movement = Math.min(1, absoluteSpeed * 8);
    const excitation = (contact.highPass * 0.34 + contact.resonance1 * 0.08) * movement;
    const output = excitation * contact.currentPressure * parameters.level * model.gain * release;
    return clamp(Number.isFinite(output) ? output : 0, -FRICTION_OUTPUT_CEILING, FRICTION_OUTPUT_CEILING);
  }

  private contact(index: number): Contact {
    return this.contacts[integer('contact index', index, 0, FRICTION_CONTACT_COUNT - 1)]!;
  }

  private createContact(seed: number): Contact {
    return {
      parameters: { ...DEFAULTS, seed },
      currentPressure: 0,
      currentSpeed: 0,
      releaseGain: 0,
      releaseSamples: 0,
      phase: 0,
      sticking: true,
      transitionCount: 0,
      rng: canonicalSeed(seed),
      previousRaw: 0,
      highPass: 0,
      colourState: 0,
      grainEnvelope: 0,
      resonance1: 0,
      resonance2: 0,
    };
  }

  private resetContact(contact: Contact): void {
    contact.currentPressure = 0;
    contact.currentSpeed = 0;
    contact.releaseGain = contact.parameters.active ? 1 : 0;
    contact.releaseSamples = 0;
    contact.phase = 0;
    contact.sticking = true;
    contact.transitionCount = 0;
    contact.rng = canonicalSeed(contact.parameters.seed);
    contact.previousRaw = 0;
    contact.highPass = 0;
    contact.colourState = 0;
    contact.grainEnvelope = 0;
    contact.resonance1 = 0;
    contact.resonance2 = 0;
  }
}

interface AdapterState {
  contactIndex: number;
  active: boolean;
  material: number;
  pressure: number;
  speed: number;
  roughness: number;
  level: number;
  pan: number;
  smoothingMs: number;
  seed: number;
}

class FrictionModuleInstance implements DspModuleInstance {
  private readonly exciter: FrictionBowExciter;
  private state: AdapterState = {
    contactIndex: 0,
    active: false,
    material: 0,
    pressure: 0,
    speed: 0,
    roughness: 0,
    level: 1,
    pan: 0,
    smoothingMs: 10,
    seed: 1,
  };

  constructor(sampleRate: number, maxBlockSize: number) {
    this.exciter = new FrictionBowExciter({ sampleRate, maxBlockSize });
  }

  reset(): void { this.exciter.reset(); }

  setParameter(id: string, value: number): void {
    const next = this.updatedState(this.state, id, value);
    this.commitState(next);
  }

  process(block: DspProcessBlock): void {
    const left = block.outputs.left;
    const right = block.outputs.right;
    if (!left || !right) throw new RangeError('left/right outputs are required');
    validateBuffers(left, right, block.frames, this.exciter.maxBlockSize);

    let next = this.state;
    const selectedContact = block.controls['contact-index'];
    if (selectedContact && selectedContact.length > 0) {
      next = this.updatedState(next, 'contact-index', selectedContact[0]!);
    }
    for (const [id, values] of Object.entries(block.controls)) {
      if (id !== 'contact-index' && values.length > 0) next = this.updatedState(next, id, values[0]!);
    }
    if (next !== this.state) this.commitState(next);
    this.exciter.processInto(left, right, block.frames);
  }

  private updatedState(previous: Readonly<AdapterState>, id: string, value: number): AdapterState {
    if (id === 'contact-index') {
      return this.stateForContact(integer(id, value, 0, FRICTION_CONTACT_COUNT - 1));
    }
    const next = { ...previous };
    if (id === 'active') {
      if (value !== 0 && value !== 1) throw new RangeError('active must be 0 or 1');
      next.active = value === 1;
    } else if (id === 'material') next.material = integer(id, value, 0, FRICTION_MATERIALS.length - 1);
    else if (id === 'pressure') next.pressure = bounded(id, value, 0, 1);
    else if (id === 'speed') next.speed = bounded(id, value, -1, 1);
    else if (id === 'roughness') next.roughness = bounded(id, value, 0, 1);
    else if (id === 'level') next.level = bounded(id, value, 0, 1);
    else if (id === 'pan') next.pan = bounded(id, value, -1, 1);
    else if (id === 'smoothing-ms') next.smoothingMs = bounded(id, value, 0, 100);
    else if (id === 'seed') next.seed = integer(id, value, 0, 0xffff_ffff);
    else throw new RangeError(`unknown parameter: ${id}`);
    return next;
  }

  private stateForContact(contactIndex: number): AdapterState {
    const parameters = this.exciter.getContactParameters(contactIndex);
    return {
      contactIndex,
      active: parameters.active,
      material: FRICTION_MATERIALS.indexOf(parameters.material),
      pressure: parameters.pressure,
      speed: parameters.speed,
      roughness: parameters.roughness,
      level: parameters.level,
      pan: parameters.pan,
      smoothingMs: parameters.smoothingTimeSeconds * 1000,
      seed: parameters.seed,
    };
  }

  private commitState(next: AdapterState): void {
    this.exciter.setContactParameters(next.contactIndex, {
      active: next.active,
      material: FRICTION_MATERIALS[next.material]!,
      pressure: next.pressure,
      speed: next.speed,
      roughness: next.roughness,
      level: next.level,
      pan: next.pan,
      smoothingTimeSeconds: next.smoothingMs / 1000,
      seed: next.seed,
    });
    this.state = next;
  }
}

export const mod012 = defineDspModule({
  descriptor: {
    id: 'mir.mod-012',
    name: 'Friction bow exciter',
    version: '0.1.0',
    inputs: [],
    outputs: [
      { id: 'left', kind: 'audio', channels: 1 },
      { id: 'right', kind: 'audio', channels: 1 },
    ],
    parameters: [
      { id: 'contact-index', defaultValue: 0, minValue: 0, maxValue: 7, unit: 'index' },
      { id: 'active', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'boolean' },
      { id: 'material', defaultValue: 0, minValue: 0, maxValue: 4, unit: 'enum' },
      { id: 'pressure', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'speed', defaultValue: 0, minValue: -1, maxValue: 1, unit: 'ratio' },
      { id: 'roughness', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'level', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'pan', defaultValue: 0, minValue: -1, maxValue: 1, unit: 'ratio' },
      { id: 'smoothing-ms', defaultValue: 10, minValue: 0, maxValue: 100, unit: 'ms' },
      { id: 'seed', defaultValue: 1, minValue: 0, maxValue: 0xffff_ffff, unit: 'uint32' },
    ],
  },
  createInstance: ({ sampleRate, maxBlockSize }) => new FrictionModuleInstance(sampleRate, maxBlockSize),
});

export default mod012;
